import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAdminDashboard } from '../../services/analytics';
import { getUsers, updateUser, deleteUser, getCategories, createCategory, updateCategory, deleteCategory, getLearningPaths, createLearningPath, updateLearningPath, deleteLearningPath } from '../../services/admin';
import api from '../../services/api';
import {
  Users, GraduationCap, BookOpen, Settings, BarChart3, Layers, Route, Trash2, Edit3, Plus, X, Search, ChevronDown,
  Eye, EyeOff, CheckCircle, AlertTriangle
} from 'lucide-react';

function StatCard({ icon, label, value, color }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700">
      <div className={`w-10 h-10 rounded-xl bg-${color}-100 dark:bg-${color}-900/30 text-${color}-600 dark:text-${color}-300 flex items-center justify-center text-lg mb-3`}>{icon}</div>
      <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</div>
      <div className="text-sm text-gray-500 dark:text-gray-400">{label}</div>
    </div>
  );
}

/* ─── Overview Tab ─── */
function OverviewTab({ data }) {
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={<Users className="w-5 h-5" />} label="Jami foydalanuvchilar" value={data?.total_users || 0} color="blue" />
        <StatCard icon={<Users className="w-5 h-5" />} label="O'qituvchilar" value={data?.total_teachers || 0} color="green" />
        <StatCard icon={<GraduationCap className="w-5 h-5" />} label="Talabalar" value={data?.total_students || 0} color="purple" />
        <StatCard icon={<BookOpen className="w-5 h-5" />} label="Kurslar" value={data?.total_courses || 0} color="orange" />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
          <h2 className="font-bold text-gray-900 dark:text-gray-100 mb-4">So'nggi foydalanuvchilar</h2>
          <div className="space-y-3">
            {data?.recent_users?.map(u => (
              <div key={u.id} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 flex items-center justify-center text-sm font-bold">{u.name?.charAt(0)}</div>
                <div className="flex-1"><p className="text-sm font-medium text-gray-900 dark:text-gray-100">{u.name}</p><p className="text-xs text-gray-500 dark:text-gray-400">{u.email}</p></div>
                <span className="text-xs bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded-full capitalize">{u.role}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
          <h2 className="font-bold text-gray-900 dark:text-gray-100 mb-4">Platforma statistikasi</h2>
          <div className="space-y-4">
            <div className="flex justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl"><span className="text-sm text-gray-600 dark:text-gray-400">Chop etilgan kurslar</span><span className="font-bold text-gray-900 dark:text-gray-100">{data?.published_courses || 0}</span></div>
            <div className="flex justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl"><span className="text-sm text-gray-600 dark:text-gray-400">Jami yozilishlar</span><span className="font-bold text-gray-900 dark:text-gray-100">{data?.total_enrollments || 0}</span></div>
          </div>
        </div>
      </div>
    </>
  );
}

/* ─── Users Tab ─── */
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    getUsers({ search, role: roleFilter }).then(r => setUsers(r.data?.data || r.data || [])).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [search, roleFilter]);

  const handleRoleChange = async (userId, newRole) => {
    await updateUser(userId, { role: newRole });
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`"${user.name}" foydalanuvchisini o'chirmoqchimisiz?`)) return;
    try {
      await deleteUser(user.id);
      setUsers(prev => prev.filter(u => u.id !== user.id));
    } catch {}
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Qidirish..."
            className="w-full pl-9 pr-4 py-2 rounded-xl border border-gray-200 dark:border-gray-600 text-sm outline-none focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
        </div>
        <div className="relative">
          <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}
            className="px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-600 text-sm outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 appearance-none pr-8">
            <option value="">Barcha rollar</option>
            <option value="student">Talaba</option>
            <option value="teacher">O'qituvchi</option>
            <option value="admin">Admin</option>
          </select>
          <ChevronDown className="w-4 h-4 text-gray-400 absolute right-2 top-2.5 pointer-events-none" />
        </div>
      </div>
      {loading ? <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent" /></div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gray-100 dark:border-gray-700">
              <th className="py-3 px-4 text-left text-gray-500 dark:text-gray-400 font-medium">Ism</th>
              <th className="py-3 px-4 text-left text-gray-500 dark:text-gray-400 font-medium">Email</th>
              <th className="py-3 px-4 text-left text-gray-500 dark:text-gray-400 font-medium">Rol</th>
              <th className="py-3 px-4 text-left text-gray-500 dark:text-gray-400 font-medium">Sana</th>
              <th className="py-3 px-4"></th>
            </tr></thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="py-3 px-4 font-medium text-gray-900 dark:text-gray-100">{u.name}</td>
                  <td className="py-3 px-4 text-gray-500 dark:text-gray-400">{u.email}</td>
                  <td className="py-3 px-4">
                    <select value={u.role} onChange={e => handleRoleChange(u.id, e.target.value)}
                      className="text-xs border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1 outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                      <option value="student">Talaba</option>
                      <option value="teacher">O'qituvchi</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>
                  <td className="py-3 px-4 text-gray-500 dark:text-gray-400">{new Date(u.created_at).toLocaleDateString('uz')}</td>                    <td className="py-3 px-4">
                      <button onClick={() => handleDelete(u)} className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 transition" title="O'chirish"><Trash2 className="w-4 h-4" /></button>
                    </td>                </tr>
              ))}
            </tbody>
          </table>
          {users.length === 0 && <p className="text-center py-8 text-gray-400">Foydalanuvchilar topilmadi</p>}
        </div>
      )}
    </div>
  );
}

/* ─── Courses Tab ─── */
function CoursesTab() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/courses').then(r => setCourses(r.data?.data || r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const toggleStatus = async (course) => {
    const newStatus = course.status === 'published' ? 'draft' : 'published';
    try {
      await api.put(`/courses/${course.id}`, { status: newStatus });
      setCourses(prev => prev.map(c => c.id === course.id ? { ...c, status: newStatus } : c));
    } catch {}
  };

  if (loading) return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent" /></div>;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
      <h2 className="font-bold text-gray-900 dark:text-gray-100 mb-4">Barcha kurslar ({courses.length})</h2>
      <div className="space-y-3">
        {courses.map(c => (
          <div key={c.id} className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
            {c.image_url ? (
              <img src={c.image_url} alt="" className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />
            ) : (
              <div className="w-14 h-14 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0"><BookOpen className="w-6 h-6 text-blue-500" /></div>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{c.title}</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">{c.teacher?.name || 'Noma\'lum'} · {c.enrollments_count ?? c.students_count ?? 0} talaba · ⭐ {c.rating ? Number(c.rating).toFixed(1) : '—'}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${c.status === 'published' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'}`}>
                {c.status === 'published' ? 'Nashr' : 'Qoralama'}
              </span>
              <button onClick={() => toggleStatus(c)} title={c.status === 'published' ? 'Yashirish' : 'Nashr qilish'}
                className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-500 dark:text-gray-400 transition">
                {c.status === 'published' ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        ))}
        {courses.length === 0 && <p className="text-center py-8 text-gray-400">Kurslar yo'q</p>}
      </div>
    </div>
  );
}

/* ─── Categories Tab ─── */
function CategoriesTab() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', slug: '', icon: '', description: '' });

  const load = () => { setLoading(true); getCategories().then(r => setCategories(r.data?.data || r.data || [])).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const openEdit = (cat) => { setEditing(cat); setForm({ name: cat.name, slug: cat.slug || '', icon: cat.icon || '', description: cat.description || '' }); setShowForm(true); };
  const openNew = () => { setEditing(null); setForm({ name: '', slug: '', icon: '', description: '' }); setShowForm(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    const slug = form.slug || form.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    if (editing) {
      await updateCategory(editing.id, { ...form, slug });
    } else {
      await createCategory({ ...form, slug });
    }
    setShowForm(false);
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Kategoriyani o\'chirmoqchimisiz?')) return;
    await deleteCategory(id);
    load();
  };

  if (loading) return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-bold text-gray-900 dark:text-gray-100">Kategoriyalar ({categories.length})</h2>
        <button onClick={openNew} className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 flex items-center gap-1.5"><Plus className="w-4 h-4" /> Yangi</button>
      </div>

      {showForm && (
        <form onSubmit={handleSave} className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700 shadow-sm space-y-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">{editing ? 'Tahrirlash' : 'Yangi kategoriya'}</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Nomi *" required
              className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 text-sm outline-none focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
            <input value={form.icon} onChange={e => setForm({...form, icon: e.target.value})} placeholder="Icon (emoji, masalan: 💻)"
              className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 text-sm outline-none focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
          </div>
          <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Tavsif" rows={2}
            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 text-sm outline-none focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 resize-none" />
          <div className="flex gap-2">
            <button type="submit" className="px-5 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">{editing ? 'Saqlash' : 'Yaratish'}</button>
            <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600">Bekor</button>
          </div>
        </form>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm divide-y divide-gray-100 dark:divide-gray-700">
        {categories.map(cat => (
          <div key={cat.id} className="flex items-center gap-3 p-4">
            <span className="text-xl">{cat.icon || '📂'}</span>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{cat.name}</h3>
              {cat.description && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{cat.description}</p>}
            </div>
            <span className="text-xs text-gray-400">{cat.courses_count ?? 0} kurs</span>
            <button onClick={() => openEdit(cat)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"><Edit3 className="w-4 h-4" /></button>
            <button onClick={() => handleDelete(cat.id)} className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {categories.length === 0 && <p className="text-center py-8 text-gray-400">Kategoriyalar yo'q</p>}
      </div>
    </div>
  );
}

/* ─── Learning Paths Tab ─── */
function LearningPathsTab() {
  const [paths, setPaths] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', difficulty: 'beginner', courses: [] });

  const load = () => {
    setLoading(true);
    Promise.all([getLearningPaths(), api.get('/courses')]).then(([pr, cr]) => {
      setPaths(pr.data || []);
      setCourses(cr.data?.data || cr.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openEdit = (p) => { setEditing(p); setForm({ title: p.title, description: p.description || '', difficulty: p.difficulty || 'beginner', courses: p.courses || [] }); setShowForm(true); };
  const openNew = () => { setEditing(null); setForm({ title: '', description: '', difficulty: 'beginner', courses: [] }); setShowForm(true); };

  const toggleCourse = (id) => {
    setForm(prev => ({ ...prev, courses: prev.courses.includes(id) ? prev.courses.filter(c => c !== id) : [...prev.courses, id] }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (editing) {
      await updateLearningPath(editing.id, form);
    } else {
      await createLearningPath(form);
    }
    setShowForm(false);
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("O'quv yo'lini o'chirmoqchimisiz?")) return;
    await deleteLearningPath(id);
    load();
  };

  if (loading) return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-bold text-gray-900 dark:text-gray-100">O'quv yo'llari ({paths.length})</h2>
        <button onClick={openNew} className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 flex items-center gap-1.5"><Plus className="w-4 h-4" /> Yangi</button>
      </div>

      {showForm && (
        <form onSubmit={handleSave} className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700 shadow-sm space-y-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">{editing ? 'Tahrirlash' : "Yangi o'quv yo'li"}</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
          </div>
          <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Sarlavha *" required
            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 text-sm outline-none focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
          <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Tavsif" rows={2}
            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 text-sm outline-none focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 resize-none" />
          <div className="relative">
            <select value={form.difficulty} onChange={e => setForm({...form, difficulty: e.target.value})}
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 text-sm outline-none focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 appearance-none pr-10">
              <option value="beginner">Boshlang'ich</option>
              <option value="intermediate">O'rta</option>
              <option value="advanced">Yuqori</option>
            </select>
            <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-3 pointer-events-none" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Kurslarni tanlang (tartib bo'yicha):</p>
            <div className="grid sm:grid-cols-2 gap-2 max-h-60 overflow-y-auto">
              {courses.map(c => {
                const selected = form.courses.includes(c.id);
                const idx = form.courses.indexOf(c.id);
                return (
                  <button key={c.id} type="button" onClick={() => toggleCourse(c.id)}
                    className={`flex items-center gap-2 p-2.5 rounded-xl border-2 text-left text-sm transition ${selected ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'}`}>
                    {selected && <span className="w-5 h-5 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center flex-shrink-0">{idx + 1}</span>}
                    <span className="truncate text-gray-900 dark:text-gray-100">{c.title}</span>
                  </button>
                );
              })}
            </div>
            {form.courses.length === 0 && <p className="text-xs text-red-500 mt-1">Kamida 1 ta kurs tanlang</p>}
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={form.courses.length === 0} className="px-5 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50">{editing ? 'Saqlash' : 'Yaratish'}</button>
            <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600">Bekor</button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {paths.map(p => (
          <div key={p.id} className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100">{p.title}</h3>
                {p.description && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{p.description}</p>}
                <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                  <span className={`px-2 py-0.5 rounded-full font-medium ${p.difficulty === 'beginner' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : p.difficulty === 'intermediate' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'}`}>
                    {p.difficulty === 'beginner' ? "Boshlang'ich" : p.difficulty === 'intermediate' ? "O'rta" : 'Yuqori'}
                  </span>
                  <span>{p.courses?.length || 0} ta kurs</span>
                  <span>{p.creator?.name || '—'}</span>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => openEdit(p)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"><Edit3 className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(p.id)} className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
        {paths.length === 0 && <p className="text-center py-8 text-gray-400 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700">O'quv yo'llari yo'q</p>}
      </div>
    </div>
  );
}

/* ─── Main Admin Dashboard ─── */
const tabs = [
  { key: 'overview', label: 'Umumiy', icon: BarChart3 },
  { key: 'users', label: 'Foydalanuvchilar', icon: Users },
  { key: 'courses', label: 'Kurslar', icon: BookOpen },
  { key: 'categories', label: 'Kategoriyalar', icon: Layers },
  { key: 'paths', label: "O'quv yo'llari", icon: Route },
];

export default function AdminDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const pathToTab = { '/admin/dashboard': 'overview', '/admin/users': 'users', '/admin/courses': 'courses', '/admin/categories': 'categories', '/admin/learning-paths': 'paths' };
  const tabToPath = { overview: '/admin/dashboard', users: '/admin/users', courses: '/admin/courses', categories: '/admin/categories', paths: '/admin/learning-paths' };
  const tab = pathToTab[location.pathname] || 'overview';

  const setTab = (key) => navigate(tabToPath[key] || '/admin/dashboard');

  useEffect(() => {
    getAdminDashboard().then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent"></div></div>;

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-red-600 to-pink-600 rounded-2xl p-8 text-white">
        <h1 className="text-2xl font-bold mb-2 flex items-center gap-2"><Settings className="w-6 h-6" /> Admin panel</h1>
        <p className="text-red-100">Platforma boshqaruvi</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-1.5 transition ${tab === t.key ? 'bg-blue-600 text-white shadow-sm' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab data={data} />}
      {tab === 'users' && <UsersTab />}
      {tab === 'courses' && <CoursesTab />}
      {tab === 'categories' && <CategoriesTab />}
      {tab === 'paths' && <LearningPathsTab />}
    </div>
  );
}